/**
 * Performance Monitoring Utility
 * 
 * This utility provides functions for monitoring application performance metrics
 * including Web Vitals and custom performance marks.
 */

// Types for Web Vitals metrics
export interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

// Types for performance monitoring options
export interface PerformanceMonitorOptions {
  reportToConsole?: boolean;
  reportToAnalytics?: boolean;
  sampleRate?: number;
}

// Default options
const defaultOptions: PerformanceMonitorOptions = {
  reportToConsole: true,
  reportToAnalytics: false,
  sampleRate: 0.1, // 10% of users
};

// Store for performance metrics
const metrics: Record<string, number> = {};

/**
 * Initialize performance monitoring
 * 
 * @param options Configuration options
 */
export function initPerformanceMonitoring(options: Partial<PerformanceMonitorOptions> = {}) {
  const opts = { ...defaultOptions, ...options };
  
  // Only monitor for a sample of users (controlled by sampleRate)
  if (Math.random() > (opts.sampleRate || 0.1)) {
    return;
  }
  
  // Listen for route changes to measure navigation timing
  window.addEventListener('popstate', () => {
    measureNavigationTiming();
  });
  
  // Set up observer for layout shifts
  if ('PerformanceObserver' in window) {
    try {
      // Layout Shifts (CLS)
      const clsObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
            const clsValue = (entry as any).value;
            metrics.cumulativeLayoutShift = (metrics.cumulativeLayoutShift || 0) + clsValue;
            
            if (opts.reportToConsole) {
              console.debug('[Performance] Layout shift:', clsValue);
            }
          }
        }
      });
      
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        metrics.largestContentfulPaint = lastEntry ? lastEntry.startTime : 0;
        
        if (opts.reportToConsole) {
          console.debug('[Performance] LCP:', metrics.largestContentfulPaint);
        }
      });
      
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      
      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          // Need to cast to PerformanceEventTiming to access processingStart
          const fidEntry = entry as unknown as PerformanceEventTiming;
          metrics.firstInputDelay = fidEntry.processingStart - entry.startTime;
          
          if (opts.reportToConsole) {
            console.debug('[Performance] FID:', metrics.firstInputDelay);
          }
        }
      });
      
      fidObserver.observe({ type: 'first-input', buffered: true });
      
      // Navigation timing
      measureNavigationTiming();
      
    } catch (e) {
      console.error('[Performance] Error setting up PerformanceObserver:', e);
    }
  }
}

/**
 * Measure navigation timing metrics
 */
function measureNavigationTiming() {
  if (window.performance && window.performance.timing) {
    // Wait for the page to load completely
    setTimeout(() => {
      const timing = window.performance.timing;
      
      // Calculate timing metrics
      const navigationStart = timing.navigationStart;
      
      // Time to First Byte (TTFB)
      metrics.timeToFirstByte = timing.responseStart - navigationStart;
      
      // Response time
      metrics.responseTime = timing.responseEnd - timing.requestStart;
      
      // DOM processing time
      metrics.domProcessingTime = timing.domComplete - timing.responseEnd;
      
      // DOM Content Loaded
      metrics.domContentLoaded = timing.domContentLoadedEventEnd - navigationStart;
      
      // Load Event
      metrics.loadComplete = timing.loadEventEnd - navigationStart;
      
      // Report metrics
      reportPerformanceMetrics();
    }, 0);
  }
}

/**
 * Create a custom performance mark
 * 
 * @param name Name of the performance mark
 * @param data Additional data to associate with the mark
 */
export function markPerformance(name: string, data: any = {}) {
  if (window.performance && window.performance.mark) {
    window.performance.mark(name, { detail: data });
  }
}

/**
 * Measure time between two performance marks
 * 
 * @param startMark Starting mark name
 * @param endMark Ending mark name
 * @param measureName Name for the measurement
 * @returns Duration in milliseconds
 */
export function measurePerformance(startMark: string, endMark: string, measureName: string): number {
  if (window.performance && window.performance.measure) {
    try {
      const measure = window.performance.measure(measureName, startMark, endMark);
      metrics[measureName] = measure.duration;
      return measure.duration;
    } catch (e) {
      console.error(`[Performance] Error measuring ${measureName}:`, e);
      return 0;
    }
  }
  return 0;
}

/**
 * Report all collected performance metrics
 */
function reportPerformanceMetrics() {
  // Log to console if enabled
  if (defaultOptions.reportToConsole) {
    console.table(metrics);
  }
  
  // Send to analytics if enabled
  if (defaultOptions.reportToAnalytics) {
    // In a real implementation, this would send data to an analytics service
    // For now, we'll just log that we would send the data
    console.info('[Performance] Would send metrics to analytics:', metrics);
  }
}

/**
 * Get all collected performance metrics
 * 
 * @returns Object containing all performance metrics
 */
export function getPerformanceMetrics(): Record<string, number> {
  return { ...metrics };
}

/**
 * Run a Lighthouse audit simulation
 * 
 * @returns Promise that resolves to audit results
 */
export async function runLighthouseAudit(): Promise<Record<string, any>> {
  // In a real implementation, this would integrate with Lighthouse CI or similar
  // For now, we'll just return some simulated metrics
  return {
    performance: calculatePerformanceScore(),
    accessibility: 0.95,
    'best-practices': 0.87,
    seo: 0.92,
    pwa: 0.65,
  };
}

/**
 * Calculate a simulated performance score based on collected metrics
 * 
 * @returns Performance score between 0 and 1
 */
function calculatePerformanceScore(): number {
  // This is a simplified calculation - real Lighthouse uses a more complex algorithm
  const lcp = metrics.largestContentfulPaint || 0;
  const fid = metrics.firstInputDelay || 0;
  const cls = metrics.cumulativeLayoutShift || 0;
  const ttfb = metrics.timeToFirstByte || 0;
  
  // Convert to scores (higher is better)
  const lcpScore = lcp < 2500 ? 1 : lcp < 4000 ? 0.5 : 0;
  const fidScore = fid < 100 ? 1 : fid < 300 ? 0.5 : 0;
  const clsScore = cls < 0.1 ? 1 : cls < 0.25 ? 0.5 : 0;
  const ttfbScore = ttfb < 800 ? 1 : ttfb < 1800 ? 0.5 : 0;
  
  // Weighted average
  return (lcpScore * 0.25 + fidScore * 0.25 + clsScore * 0.25 + ttfbScore * 0.25);
}
